import _ from "lodash";
import PropTypes from "prop-types";
import React, { PureComponent } from "react";

import { asDragSource } from "../../components/dragAndDrop/DraggableComponent";
import { TABLE_VIEW } from "../../constants/identifiers";
import TableRowTypes from "../../constants/tableRowTypes";
import { immutableRecords, PropTypeShapes } from "../../types";
import { getModifierKeysForEvent } from "../../utils/getModifierKeys";
import hasAncestorOfClass from "../../utils/hasAncestorOfClass";
import TableCellView from "./TableCellView";
import TableRowGutterView from "./TableRowGutterView";
import utils from "./utils";
// import debugUtils from "../../utils/debugUtils";

const clickWasGutter = e => hasAncestorOfClass(e.target, "tableRowGutter");

const dragCallbacks = {
  beginDrag: _props => {
    // TODO: Do we want to set isDragging or similar...?
  },

  canDrag: (props, dragSource, e) => {
    return props.canDrag && props.id !== TableRowTypes.EMPTY && props.type && clickWasGutter(e);
  },

  getDragSourceParams: props => {
    return {
      itemId: props.id,
      itemType: props.type,
      sourceView: props.viewId,
      sourceViewType: TABLE_VIEW
    };
  },

  getPreviewElement: props => {
    return renderItemPreview(props);
  }
};

const renderItemPreview = itemProps => {
  const cloneProps = _.clone(itemProps);
  cloneProps.canResize = false;
  cloneProps.showColumnSelector = false;
  cloneProps.isDragPreview = true;
  cloneProps.left = 0;
  cloneProps.top = 0;
  cloneProps.fromColumn = 0;
  cloneProps.toColumn = cloneProps.allColumns.size - 1;
  cloneProps.cellSelection = immutableRecords.TableCellSelectionRecord();
  cloneProps.isDragging = false;
  cloneProps.isDraggingRow = false;
  return renderItem(cloneProps);
};

const renderItem = (useProps, onGutterClick, onGutterDoubleClick) => {
  let classes = ["tableRow"];
  if (useProps.index % 2) {
    classes.push("odd");
  }
  if (useProps.isSelected) {
    classes.push("selected");
  }
  if (useProps.isFiltered) {
    classes.push("isFiltered");
  }
  if (useProps.isDragging) {
    classes.push("isDragging");
  }
  if (useProps.isDraggingRow) {
    classes.push("draggingRow");
  }
  if (useProps.isDragPreview) {
    classes.push("dragPreview");
  }
  if (useProps.id === TableRowTypes.EMPTY) {
    classes.push("emptyRow");
  }

  // Strip the visible cells back to the selected columns
  const visibleCells = useProps.cells.slice(useProps.fromColumn, useProps.toColumn);

  const cellRenderer = cells =>
    renderCells(
      useProps.index,
      useProps.allColumns,
      cells,
      useProps.isSelected,
      useProps.cellSelection,
      useProps.focusedCell,
      useProps.hasChildren,
      useProps.childrenExpanded,
      useProps.indentationLevel,
      useProps.onToggleChildren,
      useProps.calendar,
      useProps.autoAdjustEndDate,
      useProps.onValueChanged,
      useProps.onMultiValueChanged,
      useProps.moveFocus,
      useProps.onCellClick,
      useProps.onCellDoubleClick,
      useProps.onCellPopoverToggle,
      useProps.onCellCut,
      useProps.onCellCopy,
      useProps.onCellPaste,
      useProps.onCellDelete,
      useProps.getCellId,
      useProps.getCellOptions,
      useProps.registerCellFocusCallback,
      useProps.unregisterCellFocusCallback
    );

  return (
    <div
      className={classes.join(" ")}
      style={{
        left: `${useProps.left}px`,
        top: `${useProps.top}px`,
        height: `${useProps.height}px`
      }}
    >
      {useProps.showGutter ? (
        <TableRowGutterView
          icon={
            useProps.showViolatedConstraints && useProps.hasViolatedConstraints ? "statusConflict" : useProps.gutterIcon
          }
          iconColor={
            useProps.showViolatedConstraints && useProps.hasViolatedConstraints
              ? "statusError"
              : useProps.gutterIconColor
          }
          onClick={onGutterClick}
          onDoubleClick={onGutterDoubleClick}
          pulsateIconId={useProps.pulsateIconId}
        />
      ) : null}
      {cellRenderer(visibleCells)}
    </div>
  );
};

const renderCells = function(
  rowIndex,
  columns,
  cells,
  isRowSelected,
  cellSelection,
  focusedCell,
  hasChildren,
  childrenExpanded,
  indentationLevel,
  onToggleChildren,
  calendar,
  autoAdjustEndDate,
  onValueChanged,
  onMultiValueChanged,
  moveFocus,
  onCellClick,
  onCellDoubleClick,
  onCellPopoverToggle,
  onCellCut,
  onCellCopy,
  onCellPaste,
  onCellDelete,
  getCellId,
  getCellOptions,
  registerCellFocusCallback,
  unregisterCellFocusCallback
) {
  return cells
    .map(cell => {
      const col = columns.get(cell.column);
      const cellPosition = immutableRecords.TableCellPositionRecord(cell);
      const selected = utils.isCellSelected(cellSelection, cellPosition);
      const focused = Boolean(
        focusedCell && focusedCell.row === cellPosition.row && focusedCell.column === cellPosition.column
      );
      const singleSelection = selected ? utils.isCellSingleSelection(cellSelection, cellPosition) : false;
      const singleGroup = selected ? cellSelection.groups.size === 1 : false;

      return (
        <TableCellView
          {...cell.toObject()}
          // we do not need size and it is apparently changing
          size={null}
          //#!if readonly === true
          disabled={true}
          //#!endif
          cellId={getCellId(cell.row, cell.column)}
          registerCellFocusCallback={registerCellFocusCallback}
          unregisterCellFocusCallback={unregisterCellFocusCallback}
          width={col.width}
          cellType={col.cellType}
          options={getCellOptions}
          key={cell.column}
          odd={cell.column % 2 === 0}
          selected={selected}
          focused={focused}
          hasChildren={cell.column === 0 ? hasChildren : false}
          childrenExpanded={cell.column === 0 ? childrenExpanded : false}
          indentationLevel={cell.column === 0 ? indentationLevel : 0}
          onToggleChildren={onToggleChildren}
          calendar={calendar}
          autoAdjustEndDate={autoAdjustEndDate}
          onValueChanged={onValueChanged}
          onMultiValueChanged={onMultiValueChanged}
          moveFocus={moveFocus}
          onClick={onCellClick}
          onDoubleClick={onCellDoubleClick}
          onPopoverToggle={onCellPopoverToggle}
          onCellCut={onCellCut}
          onCellCopy={onCellCopy}
          onCellPaste={onCellPaste}
          onCellDelete={onCellDelete}
          singleSelection={singleSelection}
          singleGroup={singleGroup}
        />
      );
    })
    .toArray();
};

class TableRowView extends PureComponent {
  constructor(props, context) {
    super(props, context);
    this.onGutterClick = this.onGutterClick.bind(this);
    this.onGutterDoubleClick = this.onGutterDoubleClick.bind(this);
  }

  // // TODO: Delete this entire function once done with debugging
  // componentWillReceiveProps(nextProps) {
  //   debugUtils.logPropChanges(`TableRowView [${nextProps.index}]`, this.props, nextProps, true, false);
  // }

  onGutterClick(e) {
    this.props.onGutterClick(this.props.id, getModifierKeysForEvent(e));
  }

  onGutterDoubleClick() {
    if (this.props.onShowInspector) {
      this.props.onShowInspector();
    }
  }

  render() {
    return renderItem(this.props, this.onGutterClick, this.onGutterDoubleClick);
  }
}

TableRowView.propTypes = {
  index: PropTypes.number.isRequired,
  viewId: PropTypes.string.isRequired,
  tableId: PropTypes.string.isRequired,
  id: PropTypes.string,
  left: PropTypes.number.isRequired,
  top: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,

  cells: PropTypeShapes.tableCellList.isRequired,
  allColumns: PropTypeShapes.tableColumnList.isRequired,

  fromColumn: PropTypes.number.isRequired,
  toColumn: PropTypes.number.isRequired,

  isSelected: PropTypes.bool.isRequired,
  isFiltered: PropTypes.bool,

  cellSelection: PropTypes.instanceOf(immutableRecords.TableCellSelectionRecord),
  focusedCell: PropTypes.instanceOf(immutableRecords.TableCellPositionRecord),

  // Registering cells with TableView
  registerCellFocusCallback: PropTypes.func.isRequired,
  unregisterCellFocusCallback: PropTypes.func.isRequired,

  onToggleChildren: PropTypes.func,
  hasChildren: PropTypes.bool.isRequired,
  childrenExpanded: PropTypes.bool.isRequired,
  indentationLevel: PropTypes.number.isRequired,
  onValueChanged: PropTypes.func.isRequired,
  onMultiValueChanged: PropTypes.func.isRequired,

  // Used for DatePicker
  calendar: PropTypeShapes.calendar,
  autoAdjustEndDate: PropTypes.bool,

  // Used for dependencies
  getCellOptions: PropTypes.func,

  // Internal Table functions
  moveFocus: PropTypes.func.isRequired,
  getCellId: PropTypes.func.isRequired,
  onCellClick: PropTypes.func.isRequired,
  onCellDoubleClick: PropTypes.func.isRequired,
  onCellPopoverToggle: PropTypes.func.isRequired,
  onCellCut: PropTypes.func.isRequired,
  onCellCopy: PropTypes.func.isRequired,
  onCellPaste: PropTypes.func.isRequired,
  onCellDelete: PropTypes.func.isRequired,

  showGutter: PropTypes.bool,
  onGutterClick: PropTypes.func,
  gutterIcon: PropTypes.string,
  gutterIconColor: PropTypes.string,
  canDrag: PropTypes.bool,

  pulsateIconId: PropTypes.string,

  isDraggingRow: PropTypes.bool,
  showViolatedConstraints: PropTypes.bool,
  hasViolatedConstraints: PropTypes.bool,
  onShowInspector: PropTypes.func,

  // From asDragSource
  isDragging: PropTypes.bool,
  dragIsFromSameSource: PropTypes.bool
};

export const DraggableTableRowView = asDragSource(TableRowView, TABLE_VIEW, dragCallbacks);

export default TableRowView;
